import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, User, Mail, Trash2, Loader2, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserManagement() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users?authorization=${token}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [token]);

  const pendingUsers = users.filter(u => u.status === 'pending');
  const activeUsers = users.filter(u => u.status !== 'pending');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) { toast.error('Please fill in all fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/users?authorization=${token}`, formData);
      toast.success('User created successfully!');
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'staff' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}?authorization=${token}&role=${newRole}`);
      toast.success('User role updated');
      fetchUsers();
    } catch (error) { toast.error('Failed to update user'); }
  };

  const toggleUserStatus = async (userId, isActive) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}?authorization=${token}&is_active=${!isActive}`);
      toast.success(`User ${isActive ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) { toast.error('Failed to update user'); }
  };

  const approveUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/approve?authorization=${token}`);
      toast.success('User approved successfully!');
      fetchUsers();
    } catch (error) { toast.error('Failed to approve user'); }
  };

  const rejectUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/reject?authorization=${token}`);
      toast.success('User registration rejected');
      fetchUsers();
    } catch (error) { toast.error('Failed to reject user'); }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/users/${userId}?authorization=${token}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) { toast.error('Failed to delete user'); }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'delete') await deleteUser(confirmAction.userId);
    if (confirmAction.type === 'reject') await rejectUser(confirmAction.userId);
    setConfirmAction(null);
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

  const getStatusBadge = (user) => {
    if (user.status === 'pending') return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending Approval</Badge>;
    if (user.status === 'rejected') return <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
    if (!user.is_active) return <Badge variant="outline" className="text-red-600 border-red-200">Inactive</Badge>;
    return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>;
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="user-management-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-600">{users.length} registered users{pendingUsers.length > 0 && <span className="text-amber-600 font-medium"> ({pendingUsers.length} pending approval)</span>}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="add-user-btn"><Plus className="w-5 h-5 mr-2" />Add User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2"><Label htmlFor="name">Full Name</Label><Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Smith" data-testid="user-name-input" /></div>
                <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@company.com" data-testid="user-email-input" /></div>
                <div className="space-y-2"><Label htmlFor="password">Password</Label><Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Create a password" data-testid="user-password-input" /></div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 bg-primary hover:bg-primary/90" data-testid="create-user-btn">{submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create User'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Pending Approvals Section */}
        {pendingUsers.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800"><Clock className="w-5 h-5" />Pending Approvals ({pendingUsers.length})</CardTitle>
              <CardDescription className="text-amber-700">These users have registered and are waiting for your approval</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-amber-100">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="flex items-center gap-4 p-4 hover:bg-amber-50/50" data-testid={`pending-user-${user.id}`}>
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{user.name}</p>
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{user.email}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />Registered {formatDate(user.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => approveUser(user.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-user-${user.id}`}>
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmAction({ type: 'reject', userId: user.id, userName: user.name, userEmail: user.email })} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-user-${user.id}`}>
                        <XCircle className="w-4 h-4 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Users List */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-lg">All Users</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {activeUsers.map((user) => (
                <div key={user.id} className="flex items-center gap-4 p-4 hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{user.name}</p>
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className="capitalize">{user.role}</Badge>
                      {getStatusBadge(user)}
                      {user.id === currentUser?.id && <Badge variant="outline" className="text-primary border-primary/30">You</Badge>}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{user.email}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />Joined {formatDate(user.created_at)}</span>
                    </div>
                  </div>
                  {user.id !== currentUser?.id && (
                    <div className="flex items-center gap-2">
                      <Select value={user.role} onValueChange={(v) => updateUserRole(user.id, v)}>
                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={() => toggleUserStatus(user.id, user.is_active)}>
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmAction({ type: 'delete', userId: user.id, userName: user.name, userEmail: user.email })}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog for Delete/Reject */}
        <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmAction?.type === 'delete' ? 'Delete User' : 'Reject Registration'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmAction?.type === 'delete'
                  ? `Are you sure you want to delete ${confirmAction?.userName} (${confirmAction?.userEmail})? This action cannot be undone.`
                  : `Are you sure you want to reject the registration for ${confirmAction?.userName} (${confirmAction?.userEmail})? They will not be able to access the portal.`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmAction}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-action"
              >
                {confirmAction?.type === 'delete' ? 'Delete' : 'Reject'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
